import { PersonOutline, PhoneOutlined } from '@mui/icons-material';
import { Box, CircularProgress, Divider, Typography } from '@mui/material';
import { BarChart } from "@mui/x-charts/BarChart";
import { generateClient } from 'aws-amplify/api';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../contexts/UserContext';
import { esQuery } from '../../graphql/queries';

const PenetrationDialog = ({ open, campaignId, totalContacts, freshCount, suppressCount, inputStartDate, inputEndDate }) => {
	const userContext = useContext(UserContext);
	const client = generateClient();
	const [aggCounts, setAggCounts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [totalDials, setTotalDials] = useState(0);

	useEffect(() => {
		if (open && campaignId) {
			getPenetrationData();
		}
	}, [open, campaignId]);

	function returnQuery(startDate, endDate) {

		return {
			"aggs": {
				"contact": {
					"terms": {
						"field": "activityLogContactId.keyword",
						"size": 2147483647
					}
				}
			},
			"query": {
				"bool": {
					"must": [
						{
							"match_phrase": {
								"type": "Dial"
							}
						},
						{
							"term": {
								"tenant.keyword": userContext.tenantId
							}
						},
						{
							"match_phrase": {
								"activityLogCampaignId": campaignId
							}
						},
						{
							"range": {
								"createdAt": {
									"gte": startDate,
									"lte": endDate
								}
							}
						}
					],
					"must_not": [
						{
							"match_phrase": {
								"disposition": "Cadence Exhaustion - Retry Increment"
							}
						},
						{
							"match_phrase": {
								"disposition": "Agent Specific — Agent Unavailable"
							}
						}
					]
				}
			},
			"size": 0
		}
	}

	async function getPenetrationData() {
		setLoading(true);
		const response = await client.graphql({
			query: esQuery,
			variables: {
				model: "activitylog-*",
				action: "_search",
				query: JSON.stringify(returnQuery(inputStartDate, inputEndDate))
			}
		});

		const data = JSON.parse(response.data.esQuery).aggregations.contact.buckets;

		let callCount = 0;
		let attemptAggregations = {};

		// Aggregate how many times contacts have been dialed
		for (const contact of data) {
			callCount += contact.doc_count;
			attemptAggregations[contact.doc_count] = attemptAggregations[contact.doc_count] ? attemptAggregations[contact.doc_count] + 1 : 1;
		}

		const chartData = [];
		for (const key in attemptAggregations) {
			chartData.push({ contacts: key, count: attemptAggregations[key] });
		}

		if (freshCount && freshCount > 0) {
			chartData.push({ contacts: 0, count: freshCount });
		}

		if (suppressCount && suppressCount > 0) {
			chartData.push({ contacts: 'Suppressed', count: suppressCount });
		}

		// Sort the chart data by key (number of calls)
		chartData.sort((a, b) => a.contacts - b.contacts);

		setAggCounts([...chartData]);
		setTotalDials(callCount);

		setLoading(false)
	}

	return (
		<>
			<Box sx={{ display: 'flex', justifyContent: 'center' }}>

				{!loading &&
					<Box display="grid" gridTemplateColumns="2fr 1fr" gap={2} alignItems="center">
						<BarChart
							dataset={aggCounts}
							yAxis={[{ scaleType: 'band', dataKey: 'contacts' }]}
							series={[{ dataKey: 'count', color: '#b9cde8' }]}
							leftAxis={null}
							layout="horizontal"
							borderRadius={50}
							grid={{ vertical: true }}
							height={300}
							width={700}
							barLabel={(item) => {
								// Label the contact key
								const key = aggCounts[item.dataIndex].contacts;
								return `${key} Dial${key > 1 ? "s" : ""}`;
							}}
						/>
						<Box display="flex" flexDirection="column" gap={2}>
							<Box display="flex" gap={2} alignItems="center">
								<PhoneOutlined color="primary" fontSize="large" />
								<Box display="flex" flexDirection="column">
									<Typography variant="h4">{totalDials} Dials</Typography>
									{totalContacts && <Typography variant="h6">{totalContacts} Total Contacts</Typography>}
								</Box>
							</Box>
							{totalContacts && <Divider />}
							{totalContacts && <Box display="flex" gap={2} alignItems="center">
								<PersonOutline color="primary" fontSize="large" />
								<Box display="flex" flexDirection="column">
									<Typography variant="h6">Average Calls Per Contact</Typography>
									<Typography variant="h4">{(totalDials / totalContacts).toFixed(2)}</Typography>
								</Box>
							</Box>}
						</Box>


					</Box>
				}
				{loading && <CircularProgress color='primary' />}
			</Box>

		</>
	);
};

export default PenetrationDialog;
